import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import NotificationPanel from '../../components/ui/NotificationPanel';
import StatCard from '../dashboard/components/StatCard';
import QuickActionCard from '../dashboard/components/QuickActionCard';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useOrgLogo } from '../../hooks/useOrgLogo';

const MyDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [escalationAlerts, setEscalationAlerts] = useState([]);
  const [personnelRecord, setPersonnelRecord] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const { logoUrl } = useOrgLogo();

  // Sales dashboard state
  const [myQuotes, setMyQuotes] = useState([]);
  const [myScheduledTickets, setMyScheduledTickets] = useState([]);

  // Technician dashboard state
  const [myAssignedTickets, setMyAssignedTickets] = useState([]);
  const [mySchedule, setMySchedule] = useState([]);

  // Manager dashboard state
  const [allTickets, setAllTickets] = useState([]);
  const [allTicketStats, setAllTicketStats] = useState({ total: 0, open: 0, scheduledToday: 0, inProgress: 0 });

  const [stats, setStats] = useState({ quotes: 0, scheduledTickets: 0, assignedTickets: 0, todaySchedule: 0 });

  useEffect(() => {
    if (user?.email) {
      loadPersonnelAndData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadPersonnelAndData = async () => {
    try {
      const { data: personnel, error: pErr } = await supabase
        ?.from('personnel')
        ?.select('id, name, role, email, custom_role_name')
        ?.eq('email', user?.email)
        ?.single();

      if (pErr || !personnel) {
        setLoading(false);
        fetchEscalationAlerts();
        return;
      }

      setPersonnelRecord(personnel);
      setUserRole(personnel?.role);

      if (personnel?.role === 'salesperson') {
        await Promise.all([
          fetchMyQuotes(personnel?.id),
          fetchMyScheduledTickets(personnel?.id),
          fetchEscalationAlerts()
        ]);
      } else if (personnel?.role === 'manager' || personnel?.role === 'admin') {
        await Promise.all([
          fetchAllTickets(),
          fetchEscalationAlerts()
        ]);
      } else {
        // Technician or other roles
        await Promise.all([
          fetchMyAssignedTickets(personnel?.id),
          fetchMySchedule(personnel?.id),
          fetchEscalationAlerts()
        ]);
      }
    } catch (err) {
      console.error('MyDashboard load error:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyQuotes = async (personnelId) => {
    try {
      const { data, error } = await supabase
        ?.from('quotes')
        ?.select(`
          id, quote_number, total_amount, created_at, expected_service_date,
          customer:customers(name),
          status:quote_statuses(name, color)
        `)
        ?.eq('salesperson_id', personnelId)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (error) throw error;
      setMyQuotes(data || []);
      setStats(prev => ({ ...prev, quotes: data?.length || 0 }));
    } catch (err) {
      console.error('Error fetching quotes:', err?.message);
    }
  };

  const fetchMyScheduledTickets = async (personnelId) => {
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        ?.from('tickets')
        ?.select(`
          id, ticket_number, scheduled_date, description, estimated_duration,
          customer:customers(name),
          status:ticket_statuses(name, color),
          priority:ticket_priorities(name, color),
          service_type:ticket_service_types(name)
        `)
        ?.eq('salesperson_id', personnelId)
        ?.not('scheduled_date', 'is', null)
        ?.gte('scheduled_date', today?.toISOString())
        ?.order('scheduled_date', { ascending: true })
        ?.limit(10);

      if (error) throw error;
      setMyScheduledTickets(data || []);
      setStats(prev => ({ ...prev, scheduledTickets: data?.length || 0 }));
    } catch (err) {
      console.error('Error fetching scheduled tickets:', err?.message);
    }
  };

  const fetchMyAssignedTickets = async (personnelId) => {
    try {
      const { data: techTickets, error } = await supabase
        ?.from('ticket_technicians')
        ?.select(`
          ticket:tickets(
            id, ticket_number, scheduled_date, description, estimated_duration,
            customer:customers(name),
            status:ticket_statuses(name, color),
            priority:ticket_priorities(name, color),
            service_type:ticket_service_types(name)
          )
        `)
        ?.eq('technician_id', personnelId);

      if (error) throw error;

      const tickets = techTickets
        ?.map(tt => tt?.ticket)
        ?.filter(t => t && t?.status?.name?.toLowerCase() !== 'completed' && t?.status?.name?.toLowerCase() !== 'cancelled')
        ?.sort((a, b) => {
          if (!a?.scheduled_date) return 1;
          if (!b?.scheduled_date) return -1;
          return new Date(a?.scheduled_date) - new Date(b?.scheduled_date);
        }) || [];

      setMyAssignedTickets(tickets);
      setStats(prev => ({ ...prev, assignedTickets: tickets?.length || 0 }));
    } catch (err) {
      console.error('Error fetching assigned tickets:', err?.message);
    }
  };

  const fetchMySchedule = async (personnelId) => {
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      const nextWeek = new Date(today);
      nextWeek?.setDate(nextWeek?.getDate() + 7);

      const { data: techTickets, error } = await supabase
        ?.from('ticket_technicians')
        ?.select(`
          ticket:tickets(
            id, ticket_number, scheduled_date, description, estimated_duration,
            customer:customers(name),
            status:ticket_statuses(name, color),
            priority:ticket_priorities(name, color)
          )
        `)
        ?.eq('technician_id', personnelId);

      if (error) throw error;

      const scheduled = techTickets
        ?.map(tt => tt?.ticket)
        ?.filter(t => t?.scheduled_date && new Date(t?.scheduled_date) >= today && new Date(t?.scheduled_date) <= nextWeek)
        ?.sort((a, b) => new Date(a?.scheduled_date) - new Date(b?.scheduled_date)) || [];

      setMySchedule(scheduled);
      setStats(prev => ({ ...prev, todaySchedule: scheduled?.filter(t => {
        const d = new Date(t?.scheduled_date);
        const todayEnd = new Date(today);
        todayEnd?.setHours(23, 59, 59, 999);
        return d <= todayEnd;
      })?.length || 0 }));
    } catch (err) {
      console.error('Error fetching schedule:', err?.message);
    }
  };

  const fetchAllTickets = async () => {
    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      const todayEnd = new Date(today);
      todayEnd?.setHours(23, 59, 59, 999);

      const { data, error } = await supabase
        ?.from('tickets')
        ?.select(`
          id, ticket_number, scheduled_date, description, estimated_duration,
          customer:customers(name),
          status:ticket_statuses(name, color),
          priority:ticket_priorities(name, color),
          service_type:ticket_service_types(name)
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(15);

      if (error) throw error;

      const tickets = data || [];
      const openTickets = tickets?.filter(t => {
        const s = t?.status?.name?.toLowerCase();
        return s !== 'completed' && s !== 'cancelled';
      });
      const inProgress = tickets?.filter(t => t?.status?.name?.toLowerCase() === 'in progress');
      const scheduledToday = tickets?.filter(t => {
        if (!t?.scheduled_date) return false;
        const d = new Date(t?.scheduled_date);
        return d >= today && d <= todayEnd;
      });

      setAllTickets(tickets);
      setAllTicketStats({
        total: tickets?.length,
        open: openTickets?.length,
        scheduledToday: scheduledToday?.length,
        inProgress: inProgress?.length
      });
    } catch (err) {
      console.error('Error fetching all tickets:', err?.message);
    }
  };

  const fetchEscalationAlerts = async () => {
    try {
      const now = new Date();
      const { data, error } = await supabase
        ?.from('tickets')
        ?.select(`
          id, ticket_number, scheduled_date, description,
          priority:ticket_priorities(name),
          status:ticket_statuses(name),
          customer:customers(name)
        `)
        ?.not('scheduled_date', 'is', null);

      if (error) throw error;

      const alerts = [];
      data?.forEach((ticket) => {
        const priority = ticket?.priority?.name?.toLowerCase();
        const status = ticket?.status?.name?.toLowerCase();
        if (status === 'completed' || status === 'cancelled') return;

        const scheduledDate = new Date(ticket?.scheduled_date);
        const hoursOverdue = (now - scheduledDate) / (1000 * 60 * 60);
        const thresholds = { critical: 1, high: 4, medium: 24, low: 72 };
        const threshold = thresholds?.[priority] ?? 24;

        if (hoursOverdue >= threshold) {
          const hoursLabel = hoursOverdue < 24
            ? `${Math.floor(hoursOverdue)}h overdue`
            : `${Math.floor(hoursOverdue / 24)}d overdue`;

          alerts?.push({
            id: `escalation-${ticket?.id}`,
            type: priority === 'critical' || priority === 'high' ? 'urgent' : 'schedule',
            title: `Escalation: ${ticket?.ticket_number}`,
            message: `${priority?.charAt(0)?.toUpperCase() + priority?.slice(1)} priority ticket for ${ticket?.customer?.name} is ${hoursLabel}.`,
            timestamp: hoursLabel,
            icon: priority === 'critical' ? 'AlertOctagon' : priority === 'high' ? 'AlertTriangle' : 'Clock',
            actionLabel: 'View Ticket',
            actionPath: '/ticket-management'
          });
        }
      });

      setEscalationAlerts(alerts);
    } catch (error) {
      console.error('Error fetching escalation alerts:', error?.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return { month: '---', day: '--', time: 'Not scheduled' };
    const d = new Date(dateStr);
    return {
      month: d?.toLocaleString('en-US', { month: 'short' })?.toUpperCase(),
      day: d?.getDate()?.toString(),
      time: d?.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    };
  };

  const getStatusBadgeClass = (statusName) => {
    const s = statusName?.toLowerCase();
    if (s === 'scheduled') return 'bg-blue-100 text-blue-700';
    if (s === 'in progress') return 'bg-yellow-100 text-yellow-700';
    if (s === 'pending') return 'bg-gray-100 text-gray-600';
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'approved') return 'bg-green-100 text-green-700';
    if (s === 'draft') return 'bg-gray-100 text-gray-600';
    if (s === 'sent') return 'bg-blue-100 text-blue-700';
    if (s === 'converted') return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-600';
  };

  const getPriorityClass = (priorityName) => {
    const p = priorityName?.toLowerCase();
    if (p === 'critical') return 'text-error';
    if (p === 'high') return 'text-warning';
    if (p === 'medium') return 'text-primary';
    return 'text-muted-foreground';
  };

  const notifications = [
    {
      id: 1,
      type: 'urgent',
      title: 'New Urgent Request',
      message: 'Customer reported critical equipment failure requiring immediate attention',
      timestamp: '2 minutes ago',
      icon: 'AlertCircle',
      actionLabel: 'View Request',
      actionPath: '/support-request-management'
    },
    ...escalationAlerts
  ];

  const isSales = userRole === 'salesperson';
  const isManager = userRole === 'manager' || userRole === 'admin';
  const isTechnician = !isSales && !isManager;
  const displayName = personnelRecord?.name || user?.email || 'User';
  const roleLabel = personnelRecord?.custom_role_name || (userRole ? userRole?.charAt(0)?.toUpperCase() + userRole?.slice(1) : 'User');

  const salesQuickActions = [
    {
      id: 1,
      title: 'New Quote',
      description: 'Create a new quote for a customer',
      icon: 'FilePlus',
      iconColor: 'var(--color-primary)',
      actionLabel: 'Create Quote',
      onClick: () => navigate('/quote-management')
    },
    {
      id: 2,
      title: 'View All Quotes',
      description: 'Browse and manage all your quotes',
      icon: 'FileText',
      iconColor: 'var(--color-secondary)',
      actionLabel: 'Open Quotes',
      onClick: () => navigate('/quote-management')
    },
    {
      id: 3,
      title: 'Scheduled Jobs',
      description: 'View tickets scheduled for your customers',
      icon: 'CalendarDays',
      iconColor: 'var(--color-warning)',
      actionLabel: 'View Schedule',
      onClick: () => navigate('/ticket-management')
    },
    {
      id: 4,
      title: 'Customer Database',
      description: 'Access customer profiles and history',
      icon: 'Database',
      iconColor: 'var(--color-accent)',
      actionLabel: 'Browse Customers',
      onClick: () => navigate('/customer-database')
    }
  ];

  const techQuickActions = [
    {
      id: 1,
      title: 'My Tickets',
      description: 'View all tickets assigned to you',
      icon: 'ClipboardList',
      iconColor: 'var(--color-primary)',
      actionLabel: 'View Tickets',
      onClick: () => navigate('/ticket-management')
    },
    {
      id: 2,
      title: 'My Schedule',
      description: 'Check your upcoming appointments',
      icon: 'CalendarDays',
      iconColor: 'var(--color-warning)',
      actionLabel: 'Open Calendar',
      onClick: () => navigate('/ticket-management')
    },
    {
      id: 3,
      title: 'All Tickets',
      description: 'Browse all tickets in the system',
      icon: 'List',
      iconColor: 'var(--color-secondary)',
      actionLabel: 'View All Tickets',
      onClick: () => navigate('/ticket-management')
    },
    {
      id: 4,
      title: 'Customer Database',
      description: 'Access customer profiles and service history',
      icon: 'Database',
      iconColor: 'var(--color-accent)',
      actionLabel: 'Browse Customers',
      onClick: () => navigate('/customer-database')
    }
  ];

  const managerQuickActions = [
    {
      id: 1,
      title: 'Ticket Management',
      description: 'View and manage all service tickets',
      icon: 'ClipboardList',
      iconColor: 'var(--color-primary)',
      actionLabel: 'View Tickets',
      onClick: () => navigate('/ticket-management')
    },
    {
      id: 2,
      title: 'Technician Assignment',
      description: 'Assign technicians to tickets',
      icon: 'UserCog',
      iconColor: 'var(--color-secondary)',
      actionLabel: 'Assign Technicians',
      onClick: () => navigate('/technician-assignment')
    },
    {
      id: 3,
      title: 'Personnel Management',
      description: 'Manage staff and permissions',
      icon: 'Users',
      iconColor: 'var(--color-warning)',
      actionLabel: 'Manage Personnel',
      onClick: () => navigate('/personnel-management')
    },
    {
      id: 4,
      title: 'Reports',
      description: 'View performance reports and analytics',
      icon: 'BarChart3',
      iconColor: 'var(--color-accent)',
      actionLabel: 'View Reports',
      onClick: () => navigate('/reports')
    }
  ];

  const TicketTable = ({ tickets, emptyMessage }) => (
    tickets?.length === 0 ? (
      <div className="bg-card rounded-xl border border-border p-8 text-center">
        <Icon name="ClipboardList" size={32} color="var(--color-muted-foreground)" />
        <p className="mt-3 text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    ) : (
      <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
        {/* Mobile card view */}
        <div className="block sm:hidden divide-y divide-border">
          {tickets?.slice(0, 8)?.map(ticket => {
            const { month, day, time } = formatDate(ticket?.scheduled_date);
            return (
              <div key={ticket?.id} className="p-4 hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => navigate('/ticket-management')}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="text-sm font-medium text-primary">{ticket?.ticket_number}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(ticket?.status?.name)}`}>
                    {ticket?.status?.name || 'Unknown'}
                  </span>
                </div>
                <p className="text-sm text-foreground mb-1">{ticket?.customer?.name || '—'}</p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium ${getPriorityClass(ticket?.priority?.name)}`}>{ticket?.priority?.name || '—'}</span>
                  <span className="text-xs text-muted-foreground">
                    {ticket?.scheduled_date ? `${month} ${day}, ${time}` : 'Not scheduled'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        {/* Desktop table view */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Ticket #</th>
                <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Customer</th>
                <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Priority</th>
                <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Scheduled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {tickets?.slice(0, 8)?.map(ticket => {
                const { month, day, time } = formatDate(ticket?.scheduled_date);
                return (
                  <tr key={ticket?.id} className="hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => navigate('/ticket-management')}>
                    <td className="px-4 py-3"><span className="text-sm font-medium text-primary">{ticket?.ticket_number}</span></td>
                    <td className="px-4 py-3"><span className="text-sm text-foreground">{ticket?.customer?.name || '—'}</span></td>
                    <td className="px-4 py-3"><span className={`text-xs font-medium ${getPriorityClass(ticket?.priority?.name)}`}>{ticket?.priority?.name || '—'}</span></td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(ticket?.status?.name)}`}>
                        {ticket?.status?.name || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-muted-foreground">
                        {ticket?.scheduled_date ? `${month} ${day}, ${time}` : 'Not scheduled'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    )
  );

  const ScheduleList = ({ tickets }) => (
    tickets?.length === 0 ? (
      <div className="bg-card rounded-xl border border-border p-6 text-center">
        <Icon name="Calendar" size={28} color="var(--color-muted-foreground)" />
        <p className="mt-2 text-sm text-muted-foreground">No upcoming scheduled jobs.</p>
      </div>
    ) : (
      <div className="space-y-3">
        {tickets?.map(ticket => {
          const { month, day, time } = formatDate(ticket?.scheduled_date);
          return (
            <div key={ticket?.id} className="bg-card rounded-xl border border-border p-4 hover:border-primary/40 transition-colors cursor-pointer" onClick={() => navigate('/ticket-management')}>
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center justify-center w-12 flex-shrink-0 bg-primary/5 rounded-lg py-1">
                  <span className="text-xs font-caption text-muted-foreground uppercase">{month}</span>
                  <span className="text-xl font-heading font-semibold text-foreground">{day}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-medium text-primary">{ticket?.ticket_number}</span>
                    <span className={`text-xs font-medium ${getPriorityClass(ticket?.priority?.name)}`}>
                      {ticket?.priority?.name?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-xs text-foreground font-medium mb-1">{ticket?.customer?.name}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{ticket?.description}</p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={12} color="var(--color-muted-foreground)" />
                      <span className="text-xs text-muted-foreground">{time}</span>
                    </div>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getStatusBadgeClass(ticket?.status?.name)}`}>
                      {ticket?.status?.name}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="px-3 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-8 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-4 md:mb-6 lg:mb-8">
          {logoUrl && (
            <div className="flex items-center mb-3">
              <img src={logoUrl} alt="Organization logo" className="h-14 w-auto object-contain max-w-[220px]" />
            </div>
          )}
          <div className="flex flex-col gap-3 mb-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-1">
                {loading ? 'My Dashboard' : `Welcome, ${displayName}`}
              </h1>
              <div className="flex flex-wrap items-center gap-2">
                {roleLabel && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    {roleLabel}
                  </span>
                )}
                <p className="text-sm md:text-base text-muted-foreground">
                  {isSales ? 'Your quotes and scheduled jobs' : isManager ? 'All tickets and team overview' : 'Your assigned tickets and schedule'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {escalationAlerts?.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertTriangle" size={14} color="var(--color-error)" />
                  <span className="text-xs font-medium text-error">
                    {escalationAlerts?.length} escalation{escalationAlerts?.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              <Button
                variant="default"
                iconName="Bell"
                iconPosition="left"
                onClick={() => setIsNotificationPanelOpen(true)}
                className="text-sm">
                Alerts ({notifications?.length})
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-muted-foreground">Loading your dashboard...</p>
            </div>
          </div>
        ) : !personnelRecord ? (
          <div className="flex items-center justify-center py-20">
            <div className="bg-card rounded-xl border border-border p-6 sm:p-8 text-center max-w-md mx-4">
              <Icon name="UserX" size={40} color="var(--color-muted-foreground)" />
              <h3 className="mt-4 text-lg font-heading font-semibold text-foreground">No Personnel Record Found</h3>
              <p className="mt-2 text-sm text-muted-foreground">Your account ({user?.email}) is not linked to a personnel record. Contact your manager to set up your profile.</p>
              <Button variant="default" className="mt-4" onClick={() => navigate('/dashboard')}>Go to Main Dashboard</Button>
            </div>
          </div>
        ) : isSales ? (
          /* ===== SALES DASHBOARD ===== */
          (<>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 lg:gap-6 mb-4 md:mb-6 lg:mb-8">
              <StatCard title="My Quotes" value={stats?.quotes?.toString()} subtitle="Total quotes" icon="FileText" iconColor="var(--color-primary)" onClick={() => navigate('/quote-management')} trend={0} trendDirection="neutral" />
              <StatCard title="Scheduled" value={stats?.scheduledTickets?.toString()} subtitle="Upcoming tickets" icon="CalendarCheck" iconColor="var(--color-secondary)" onClick={() => navigate('/ticket-management')} trend={0} trendDirection="neutral" />
              <StatCard title="Pending" value={myQuotes?.filter(q => q?.status?.name?.toLowerCase() === 'draft' || q?.status?.name?.toLowerCase() === 'sent')?.length?.toString()} subtitle="Awaiting response" icon="Clock" iconColor="var(--color-warning)" onClick={() => navigate('/quote-management')} trend={0} trendDirection="neutral" />
              <StatCard title="Converted" value={myQuotes?.filter(q => q?.status?.name?.toLowerCase() === 'converted')?.length?.toString()} subtitle="To tickets" icon="CheckCircle" iconColor="var(--color-success)" onClick={() => navigate('/quote-management')} trend={0} trendDirection="neutral" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              <div className="lg:col-span-2 space-y-4 md:space-y-6 lg:space-y-8">
                <div>
                  <div className="flex items-center justify-between mb-3 md:mb-4">
                    <h2 className="text-base md:text-lg lg:text-xl font-heading font-semibold text-foreground">My Quotes</h2>
                    <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="right" onClick={() => navigate('/quote-management')}>View All</Button>
                  </div>
                  {myQuotes?.length === 0 ? (
                    <div className="bg-card rounded-xl border border-border p-6 sm:p-8 text-center">
                      <Icon name="FileText" size={32} color="var(--color-muted-foreground)" />
                      <p className="mt-3 text-sm text-muted-foreground">No quotes found. Create your first quote!</p>
                      <Button variant="default" size="sm" className="mt-4" onClick={() => navigate('/quote-management')}>Create Quote</Button>
                    </div>
                  ) : (
                    <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
                      {/* Mobile card view */}
                      <div className="block sm:hidden divide-y divide-border">
                        {myQuotes?.map(quote => (
                          <div key={quote?.id} className="p-4 hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => navigate('/quote-management')}>
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <span className="text-sm font-medium text-primary">{quote?.quote_number}</span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(quote?.status?.name)}`}>
                                {quote?.status?.name || 'Unknown'}
                              </span>
                            </div>
                            <p className="text-sm text-foreground mb-1">{quote?.customer?.name || '—'}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-foreground">${Number(quote?.total_amount || 0)?.toLocaleString()}</span>
                              <span className="text-xs text-muted-foreground">{quote?.created_at ? new Date(quote?.created_at)?.toLocaleDateString() : '—'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Desktop table view */}
                      <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-border bg-muted/30">
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Quote #</th>
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Customer</th>
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Amount</th>
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Status</th>
                              <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {myQuotes?.map(quote => (
                              <tr key={quote?.id} className="hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => navigate('/quote-management')}>
                                <td className="px-4 py-3"><span className="text-sm font-medium text-primary">{quote?.quote_number}</span></td>
                                <td className="px-4 py-3"><span className="text-sm text-foreground">{quote?.customer?.name || '—'}</span></td>
                                <td className="px-4 py-3"><span className="text-sm font-medium text-foreground">${Number(quote?.total_amount || 0)?.toLocaleString()}</span></td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(quote?.status?.name)}`}>
                                    {quote?.status?.name || 'Unknown'}
                                  </span>
                                </td>
                                <td className="px-4 py-3"><span className="text-xs text-muted-foreground">{quote?.created_at ? new Date(quote?.created_at)?.toLocaleDateString() : '—'}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <h2 className="text-base md:text-lg lg:text-xl font-heading font-semibold text-foreground mb-3 md:mb-4">Quick Actions</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    {salesQuickActions?.map(action => <QuickActionCard key={action?.id} {...action} />)}
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <h2 className="text-base md:text-lg lg:text-xl font-heading font-semibold text-foreground">My Scheduled Jobs</h2>
                  <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="right" onClick={() => navigate('/ticket-management')}>View All</Button>
                </div>
                <ScheduleList tickets={myScheduledTickets} />
              </div>
            </div>
          </>)
        ) : isManager ? (
          /* ===== MANAGER / ADMIN DASHBOARD ===== */
          (<>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 lg:gap-6 mb-4 md:mb-6 lg:mb-8">
              <StatCard title="Total Tickets" value={allTicketStats?.total?.toString()} subtitle="All tickets" icon="ClipboardList" iconColor="var(--color-primary)" onClick={() => navigate('/ticket-management')} trend={0} trendDirection="neutral" />
              <StatCard title="Open" value={allTicketStats?.open?.toString()} subtitle="Active tickets" icon="Zap" iconColor="var(--color-warning)" onClick={() => navigate('/ticket-management')} trend={0} trendDirection="neutral" />
              <StatCard title="Today" value={allTicketStats?.scheduledToday?.toString()} subtitle="Scheduled today" icon="CalendarCheck" iconColor="var(--color-secondary)" onClick={() => navigate('/ticket-management')} trend={0} trendDirection="neutral" />
              <StatCard title="In Progress" value={allTicketStats?.inProgress?.toString()} subtitle="Active jobs" icon="Activity" iconColor="var(--color-success)" onClick={() => navigate('/ticket-management')} trend={0} trendDirection="neutral" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              <div className="lg:col-span-2 space-y-4 md:space-y-6 lg:space-y-8">
                <div>
                  <div className="flex items-center justify-between mb-3 md:mb-4">
                    <h2 className="text-base md:text-lg lg:text-xl font-heading font-semibold text-foreground">All Tickets</h2>
                    <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="right" onClick={() => navigate('/ticket-management')}>View All</Button>
                  </div>
                  <TicketTable tickets={allTickets} emptyMessage="No tickets found in the system." />
                </div>
                <div>
                  <h2 className="text-base md:text-lg lg:text-xl font-heading font-semibold text-foreground mb-3 md:mb-4">Quick Actions</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    {managerQuickActions?.map(action => <QuickActionCard key={action?.id} {...action} />)}
                  </div>
                </div>
              </div>
              <div>
                {escalationAlerts?.length > 0 && (
                  <div className="mb-4 md:mb-6">
                    <h3 className="text-sm md:text-base font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="AlertTriangle" size={16} color="var(--color-error)" />
                      Escalation Alerts
                    </h3>
                    <div className="space-y-2">
                      {escalationAlerts?.slice(0, 5)?.map(alert => (
                        <div key={alert?.id} className="bg-error/5 border border-error/20 rounded-lg p-3 cursor-pointer hover:bg-error/10 transition-colors" onClick={() => navigate('/ticket-management')}>
                          <p className="text-xs font-medium text-error mb-1">{alert?.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-2">{alert?.message}</p>
                          <span className="text-xs text-error font-medium">{alert?.timestamp}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <h2 className="text-base md:text-lg lg:text-xl font-heading font-semibold text-foreground">Upcoming Schedule</h2>
                  <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="right" onClick={() => navigate('/ticket-management')}>Calendar</Button>
                </div>
                <ScheduleList tickets={allTickets?.filter(t => t?.scheduled_date && new Date(t?.scheduled_date) >= new Date())?.slice(0, 6)} />
              </div>
            </div>
          </>)
        ) : (
          /* ===== TECHNICIAN DASHBOARD ===== */
          (<>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 lg:gap-6 mb-4 md:mb-6 lg:mb-8">
              <StatCard title="Assigned" value={stats?.assignedTickets?.toString()} subtitle="Active tickets" icon="ClipboardList" iconColor="var(--color-primary)" onClick={() => navigate('/ticket-management')} trend={0} trendDirection="neutral" />
              <StatCard title="Today" value={stats?.todaySchedule?.toString()} subtitle="Scheduled today" icon="CalendarCheck" iconColor="var(--color-secondary)" onClick={() => navigate('/ticket-management')} trend={0} trendDirection="neutral" />
              <StatCard title="In Progress" value={myAssignedTickets?.filter(t => t?.status?.name?.toLowerCase() === 'in progress')?.length?.toString()} subtitle="Active jobs" icon="Zap" iconColor="var(--color-warning)" onClick={() => navigate('/ticket-management')} trend={0} trendDirection="neutral" />
              <StatCard title="7-Day" value={mySchedule?.length?.toString()} subtitle="Upcoming jobs" icon="Calendar" iconColor="var(--color-success)" onClick={() => navigate('/ticket-management')} trend={0} trendDirection="neutral" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              <div className="lg:col-span-2 space-y-4 md:space-y-6 lg:space-y-8">
                <div>
                  <div className="flex items-center justify-between mb-3 md:mb-4">
                    <h2 className="text-base md:text-lg lg:text-xl font-heading font-semibold text-foreground">My Assigned Tickets</h2>
                    <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="right" onClick={() => navigate('/ticket-management')}>View All Tickets</Button>
                  </div>
                  <TicketTable tickets={myAssignedTickets} emptyMessage={personnelRecord ? 'No tickets currently assigned to you.' : 'No personnel record found for your account.'} />
                </div>
                <div>
                  <h2 className="text-base md:text-lg lg:text-xl font-heading font-semibold text-foreground mb-3 md:mb-4">Quick Actions</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    {techQuickActions?.map(action => <QuickActionCard key={action?.id} {...action} />)}
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <h2 className="text-base md:text-lg lg:text-xl font-heading font-semibold text-foreground">My Schedule (7 days)</h2>
                  <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="right" onClick={() => navigate('/ticket-management')}>Calendar</Button>
                </div>
                <ScheduleList tickets={mySchedule} />
              </div>
            </div>
          </>)
        )}
      </main>
      <NotificationPanel
        isOpen={isNotificationPanelOpen}
        onClose={() => setIsNotificationPanelOpen(false)}
        notifications={notifications}
      />
    </div>
  );
};

export default MyDashboard;
